import { useConvexClient } from 'convex-vue';
import { isAuthRetryableFetchError, isAuthRefreshDiscardedError } from '@supabase/supabase-js';
import { useSupabaseClient } from './useSupabaseClient';
import { useToast } from './useToast';
import { claimActiveSessionIfPending, handleSessionRevoked } from './useSingleDeviceSession';
import { api } from '../../convex/_generated/api';

const { supabase } = useSupabaseClient();
let refreshPromise: ReturnType<typeof supabase.auth.refreshSession> | null = null;

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = supabase.auth.refreshSession().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export function useConvexAuth() {
  const client = useConvexClient();
  const { createToast } = useToast();

  const fetchToken = async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
    try {
      const { data: { session }, error } = forceRefreshToken
        ? await refreshSession()
        : await supabase.auth.getSession();
      if (error) {
        // A concurrent refresh (e.g. auth-js's own auto-refresh when the tab
        // regains focus) rotated the session mid-flight, so this forced refresh
        // was discarded — not revoked. Hand back the freshly-committed token
        // instead of signing out.
        if (isAuthRefreshDiscardedError(error)) {
          const { data } = await supabase.auth.getSession();
          return data.session?.access_token ?? null;
        }
        if (isAuthRetryableFetchError(error)) {
          console.warn('[Auth] Transient session-token refresh failure:', error);
          return null; // Convex will retry
        }
        // Session revoked or expired (e.g. signed in on a newer device) — sign out.
        console.error('[Auth] Session revoked or expired:', error);
        handleSessionRevoked();
        return null;
      }
      return session?.access_token ?? null;
    } catch (error) {
      createToast({
        type: 'error',
        message: 'Unable to refresh authentication session.',
        duration: 5000,
      });
      console.error('[Auth] Failed to fetch Supabase session token:', error);
      return null;
    }
  };

  client.setAuth(fetchToken);

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      client.setAuth(async () => null);
    } else if (event === 'SIGNED_IN') {
      client.setAuth(fetchToken);
      // Fire-and-forget: ensure user record exists in Convex without blocking auth flow
      if (session?.user) {
        client.mutation(api.users.upsertUser, {
          email: session.user.email,
          displayName:
            session.user.user_metadata?.name ??
            session.user.user_metadata?.full_name ??
            undefined,
        }).then(() => {
          // On a genuine sign-in, claim this device as the single active session
          claimActiveSessionIfPending(client);
        }).catch((err) => {
          console.error('[Auth] Failed to upsert user:', err);
        });
      }
    }
  });
}
